/**
 * 规则校验 —— 动作合法性判定
 *
 * 职责：
 *   · 判断某动作在当前状态下是否合法（canXxx 系列）
 *   · 给出不合法的原因（便于 UI 提示 / 调试）
 *
 * 设计原则：**校验与状态变更分离**
 *   canApply(state, action) → 合法性
 *   apply(state, action)    → 新状态（不校验）
 */

import {
  COPIES_PER_TILE,
  indexToName,
  isHonor,
  tileIndex,
  type Tile,
} from '../tile.js';
import { shanten } from '../shanten.js';
import { isAgari, decomposeAll } from '../agari.js';
import { analyzeWaits } from '../wait.js';
import { detectYaku } from '../yaku/index.js';
import { mentsuTiles } from '../yaku/checkers.js';
import type { WinContext } from '../yaku/types.js';
import type { Action, ActionValidation, GameState, Seat } from './state.js';
import { dealerSeat, nextSeat, seatToWind } from './state.js';
import { countInHand } from './engine.js';
import { canNineTerminals, checkTripleRon } from './flow.js';
import { rulesOf } from './rule-config.js';

// ════════════════════════════════════════════════════════════════
// 工具
// ════════════════════════════════════════════════════════════════

/** 手牌 → counts（34 长度） */
export function handToCounts(hand: readonly Tile[]): number[] {
  const counts = new Array(34).fill(0);
  for (const t of hand) counts[tileIndex(t)]++;
  return counts;
}

/** 全部可见牌（自己的手牌 + 所有舍牌 + 所有副露） */
export function visibleTiles(state: GameState, seat: Seat): number[] {
  const counts = new Array(34).fill(0);
  const me = state.players[seat];

  // 自己的手牌
  for (const t of me.hand) counts[tileIndex(t)]++;

  // 所有舍牌
  for (const p of state.players) {
    for (const t of p.discards) counts[tileIndex(t)]++;
  }

  // 所有副露
  for (const p of state.players) {
    for (const m of p.melds) {
      if (m.kind === 'shuntsu') {
        counts[m.start]++;
        counts[m.start + 1]++;
        counts[m.start + 2]++;
      } else {
        counts[m.start] += m.kind === 'kantsu' ? 4 : 3;
      }
    }
  }

  // 宝牌指示牌（只算已翻开的；未翻开的指示牌对玩家不可见，不该计入可见牌）
  for (const t of state.wall.doraIndicators.slice(0, state.wall.doraRevealed)) counts[tileIndex(t)]++;

  return counts;
}

/** 剩余枚数（未见的牌） */
export function remainingCount(state: GameState, seat: Seat, tile: number): number {
  const visible = visibleTiles(state, seat);
  return Math.max(0, COPIES_PER_TILE - visible[tile]);
}

// ════════════════════════════════════════════════════════════════
// 打牌合法性
// ════════════════════════════════════════════════════════════════

/**
 * 能否打这张牌
 */
export function canDiscard(state: GameState, tile: Tile): ActionValidation {
  if (state.phase !== 'discard') {
    return { legal: false, reason: `当前阶段 ${state.phase} 不能打牌` };
  }
  const player = state.players[state.activeSeat];
  if (!player.hand.some((t) => t.id === tile.id)) {
    return { legal: false, reason: '手牌中没有这张牌' };
  }
  // 立直后只能打刚摸的牌（摸切），且不能改变手牌构成
  if (player.riichi) {
    // ⚠️⚠️ 必须用 state.drawnTileId 定位摸到的牌
    //    不能用 hand[hand.length-1] —— doDraw 会 sort(compareTile)，
    //    排序后摸到的牌【不在最后】，导致「自动摸切」打错牌（踩过）。
    const drawn =
      state.drawnTileId != null
        ? player.hand.find((t) => t.id === state.drawnTileId)
        : undefined;

    // 找不到摸牌记录（异常状态）→ 不限制，避免死锁
    if (!drawn) {
      return { legal: true };
    }

    const drawnIdx = tileIndex(drawn);
    const tileIdx = tileIndex(tile);

    if (tile.id === drawn.id) {
      return { legal: true }; // 摸切
    }

    // 打同种牌：必须手上确实有 2 张以上该种（即摸到的+原有的）
    const sameKindInHand = countInHand(player.hand, drawnIdx);
    if (tileIdx === drawnIdx && sameKindInHand >= 2) {
      return { legal: true }; // 合法的「打同种牌」换听
    }

    return {
      legal: false,
      reason: '立直后只能摸切（打刚摸的牌），或同种牌二选一',
    };
  }
  return { legal: true };
}

// ════════════════════════════════════════════════════════════════
// 立直合法性
// ════════════════════════════════════════════════════════════════

/**
 * 能否宣言立直
 *
 * 条件：
 *   1. 门清（无副露，暗杠也算副露但立直后允许暗杠 —— 简化：有副露就不能立直）
 *   2. 尚未立直
 *   3. 点数 ≥ 1000
 *   4. 牌山剩余 ≥ 4 张（保证还有摸牌机会）
 *   5. 打掉某张牌后能听牌（这一步在 canRiichiWithDiscard 中检查）
 */
export function canRiichi(state: GameState): ActionValidation {
  if (state.phase !== 'discard') {
    return { legal: false, reason: '只能在打牌前宣言立直' };
  }
  const player = state.players[state.activeSeat];
  if (player.riichi) return { legal: false, reason: '已经立直' };
  if (player.melds.some((m) => m.open)) {
    return { legal: false, reason: '副露后不能立直' };
  }
  if (player.score < 1000) {
    return { legal: false, reason: '点数不足 1000，不能立直' };
  }
  if (state.wall.live.length < 4) {
    return { legal: false, reason: '牌山剩余不足 4 张，不能立直' };
  }

  // ⚠️ 必须检查「打掉某张后是否能听牌」
  //    （踩过：只检查前几条 → 未听牌时按钮也可用，点了没反应像卡死）
  const counts = handToCounts(player.hand);
  const hasTenpaiDiscard = player.hand.some((t) => {
    const reduced = [...counts];
    reduced[tileIndex(t)]--;
    return shanten(reduced, player.melds.length) === 0;
  });
  if (!hasTenpaiDiscard) {
    return { legal: false, reason: '打牌后无法听牌，不能立直' };
  }

  return { legal: true };
}

/**
 * 打掉某张牌后能否立直（即打掉后手牌听牌）
 */
export function canRiichiWithDiscard(state: GameState, tile: Tile): ActionValidation {
  const base = canRiichi(state);
  if (!base.legal) return base;

  const player = state.players[state.activeSeat];
  const after = player.hand.filter((t) => t.id !== tile.id);
  if (after.length !== 13) {
    return { legal: false, reason: '立直时手牌必须为 14 张（打牌后 13 张听牌）' };
  }
  const counts = handToCounts(after);
  if (shanten(counts, player.melds.length) !== 0) {
    return { legal: false, reason: '打掉这张牌后未听牌，不能立直' };
  }
  return { legal: true };
}

/** 列出所有可立直的打牌选择 */
export function riichiDiscardOptions(state: GameState): Tile[] {
  if (!canRiichi(state).legal) return [];
  const player = state.players[state.activeSeat];
  const seen = new Set<number>();
  const result: Tile[] = [];
  for (const t of player.hand) {
    const idx = tileIndex(t);
    if (seen.has(idx)) continue; // 同种牌只列一次
    if (canRiichiWithDiscard(state, t).legal) {
      seen.add(idx);
      result.push(t);
    }
  }
  return result;
}

// ════════════════════════════════════════════════════════════════
// 鸣牌合法性
// ════════════════════════════════════════════════════════════════

/**
 * 能否碰
 */
export function canPon(state: GameState, seat: Seat): ActionValidation {
  if (state.phase !== 'claim') {
    return { legal: false, reason: '当前不能鸣牌' };
  }
  const discard = state.lastDiscard;
  if (!discard) return { legal: false, reason: '没有可鸣的牌' };
  if (discard.bySeat === seat) return { legal: false, reason: '不能鸣自己的牌' };

  const player = state.players[seat];
  const count = countInHand(player.hand, tileIndex(discard.tile));
  if (count < 2) {
    return { legal: false, reason: `手中只有 ${count} 张 ${indexToName(tileIndex(discard.tile))}` };
  }

  // 立直后不能鸣牌
  if (player.riichi) return { legal: false, reason: '立直后不能鸣牌' };

  // 牌山剩余 ≥ 1（保证能继续打牌）
  if (state.wall.live.length < 1) {
    return { legal: false, reason: '牌山已空' };
  }

  return { legal: true };
}

/**
 * 能否吃
 *
 * 吃必须满足：
 *   1. 只能吃上家（自己左手的玩家 = prevSeat）
 *   2. 手牌中有能与弃牌组成顺子的两张
 *   3. 不能因吃导致食替（同一巡打出刚吃的牌）
 */
export function canChi(state: GameState, seat: Seat, tiles: readonly Tile[]): ActionValidation {
  if (state.phase !== 'claim') {
    return { legal: false, reason: '当前不能鸣牌' };
  }
  const discard = state.lastDiscard;
  if (!discard) return { legal: false, reason: '没有可鸣的牌' };

  // 只能吃上家
  const upperSeat = ((seat + 3) % 4) as Seat;
  if (discard.bySeat !== upperSeat) {
    return { legal: false, reason: '只能吃上家的牌' };
  }

  const player = state.players[seat];
  if (player.riichi) return { legal: false, reason: '立直后不能鸣牌' };
  if (tiles.length !== 2) return { legal: false, reason: '吃需要 2 张手牌' };

  // 弃牌必须是数牌
  const dIdx = tileIndex(discard.tile);
  if (isHonor(dIdx)) return { legal: false, reason: '字牌不能吃' };

  // 检查 3 张能否组成顺子
  const all = [...tiles.map(tileIndex), dIdx].sort((a, b) => a - b);
  const [a, b, c] = all;
  if (c - b !== 1 || b - a !== 1) {
    return { legal: false, reason: '这三张牌不能组成顺子' };
  }
  if (Math.floor(a / 9) !== Math.floor(c / 9)) {
    return { legal: false, reason: '顺子必须同花色' };
  }

  // 手牌中确实有这 2 张
  const handCounts = handToCounts(player.hand);
  const need: number[] = [];
  for (const t of tiles) need.push(tileIndex(t));
  const temp = [...handCounts];
  for (const n of need) {
    if (temp[n] <= 0) return { legal: false, reason: '手牌中没有指定的牌' };
    temp[n]--;
  }

  return { legal: true };
}

/**
 * 能否明杠（碰杠）
 */
export function canMinkan(state: GameState, seat: Seat): ActionValidation {
  if (state.phase !== 'claim') return { legal: false, reason: '当前不能鸣牌' };
  const discard = state.lastDiscard;
  if (!discard) return { legal: false, reason: '没有可鸣的牌' };
  if (discard.bySeat === seat) return { legal: false, reason: '不能鸣自己的牌' };

  const player = state.players[seat];
  if (player.riichi) return { legal: false, reason: '立直后不能鸣牌' };

  const count = countInHand(player.hand, tileIndex(discard.tile));
  if (count < 3) return { legal: false, reason: '手中不足 3 张，不能明杠' };

  if (state.wall.dead.length === 0) return { legal: false, reason: '岭上牌已用尽' };

  return { legal: true };
}

/**
 * 能否暗杠（手牌 4 张，或已有碰 + 摸到第 4 张）
 */
export function canAnkan(state: GameState, tile: Tile): ActionValidation {
  if (state.phase !== 'discard') {
    return { legal: false, reason: '只能在打牌前暗杠' };
  }
  const player = state.players[state.activeSeat];
  const count = countInHand(player.hand, tileIndex(tile));
  if (count < 4) {
    return { legal: false, reason: `手中只有 ${count} 张，不能暗杠` };
  }

  // 立直后暗杠：只能杠不改变听牌形态的牌（简化：允许）
  // 严格规则：立直后暗杠不能改变听牌张数/种类，这里简化处理

  if (state.wall.dead.length === 0) return { legal: false, reason: '岭上牌已用尽' };
  return { legal: true };
}

/**
 * 能否加杠（已有碰 + 摸到第 4 张）
 */
export function canKakan(state: GameState, tile: Tile): ActionValidation {
  if (state.phase !== 'discard') {
    return { legal: false, reason: '只能在打牌前加杠' };
  }
  const player = state.players[state.activeSeat];
  const idx = tileIndex(tile);
  const hasPon = player.melds.some((m) => m.kind === 'koutsu' && m.start === idx);
  if (!hasPon) return { legal: false, reason: '没有对应的碰，不能加杠' };
  if (countInHand(player.hand, idx) < 1) {
    return { legal: false, reason: '手中没有这张牌' };
  }
  if (state.wall.dead.length === 0) return { legal: false, reason: '岭上牌已用尽' };
  return { legal: true };
}

// ════════════════════════════════════════════════════════════════
// 和牌合法性（含振听）
// ════════════════════════════════════════════════════════════════

/**
 * 是否有役（「无役不能和」判定）
 *
 * 遍历所有和了形分解，只要有一种分解能判出**非空役种**即算有役。
 * ⚠️ 宝牌不算役 —— 只有宝牌、没有役种不能和（原实现只查分解、漏了这条，
 *    导致无役自摸算出 ±0、结算面板空白）。
 *
 * @param isTsumo 自摸 true（和了牌已在手牌中）；荣和 false（和了牌不在手牌中，需临时补入）
 */
function hasYaku(state: GameState, seat: Seat, winTile: Tile, isTsumo: boolean): boolean {
  const player = state.players[seat];
  const effectiveHand = isTsumo ? player.hand : [...player.hand, winTile];
  const counts = handToCounts(effectiveHand);

  const ds = decomposeAll(counts, player.melds);
  if (ds.length === 0) return false;

  // ⚠️ allTiles 必须含副露的牌，否则「看全牌」的役（清老头/字一色/断幺/混一/清一）会误判
  const allTiles = [...counts];
  for (const m of player.melds) {
    for (const t of mentsuTiles(m)) allTiles[t]++;
  }

  const isClosed = player.melds.filter((m) => m.open).length === 0;
  const wt = tileIndex(winTile);
  const winCtx: WinContext = {
    roundWind: state.round.roundWind,
    seatWind: seatToWind(seat, state.round),
    winMethod: isTsumo ? 'tsumo' : 'ron',
    isRiichi: player.riichi,
    dora: { doraIndicators: state.wall.doraIndicators.map(tileIndex) },
    winTile: wt,
    honba: state.round.honba,
  };

  return ds.some(
    (d) =>
      detectYaku({
        decomposition: d,
        counts,
        allTiles,
        meldCount: player.melds.length,
        isClosed,
        winTile: wt,
        context: winCtx,
      }).yaku.length > 0,
  );
}

/**
 * 能否自摸和牌
 *
 * 注意：自摸不受振听限制
 */
export function canTsumo(state: GameState): ActionValidation {
  if (state.phase !== 'discard') return { legal: false, reason: '当前不能自摸' };
  const player = state.players[state.activeSeat];
  // ⚠️ 有副露时暗手牌**不是** 14 张：每组副露占 3 张（杠也只算 3 张，第 4 张在副露里）
  //    → 和了形应为 (14 - 3 × 副露数) 张。
  //    写死 14 会导致「只要吃过/碰过就永远无法自摸」（踩过）。
  const concealedTarget = 14 - 3 * player.melds.length;
  if (player.hand.length !== concealedTarget) {
    return {
      legal: false,
      reason: `手牌 ${player.hand.length} 张，应为 ${concealedTarget} 张（副露 ${player.melds.length} 组），不能和`,
    };
  }

  // ⚠️ 必须做真正的和了判定（踩过：只检查张数 → 任何时候点自摸都能"胡牌"）
  const counts = handToCounts(player.hand);
  if (!isAgari(counts, player.melds.length)) {
    return { legal: false, reason: '手牌未和（不是 4 面子 + 1 雀头）' };
  }

  // ⚠️ 无役不能和：原来这里只有注释、没有实现（无役自摸会算出 ±0）。
  //    门清自摸本身即「门前清自摸和」一役 → 门清永远合法；
  //    只有「副露且无其他役」的手才会被挡。
  //    和了牌必须用 drawnTileId 定位（doDraw 会排序，手牌末张不一定是摸的牌）。
  const drawn =
    state.drawnTileId != null
      ? player.hand.find((t) => t.id === state.drawnTileId)
      : undefined;
  const winTile = drawn ?? player.hand[player.hand.length - 1];
  if (!hasYaku(state, state.activeSeat, winTile, true)) {
    return { legal: false, reason: '无役不能和（没有役种；宝牌不算役）' };
  }

  return { legal: true };
}

/**
 * 能否荣和（含振听检查）
 */
export function canRon(state: GameState, seat: Seat): ActionValidation {
  if (state.phase !== 'claim') return { legal: false, reason: '当前不能荣和' };
  const discard = state.lastDiscard;
  if (!discard) return { legal: false, reason: '没有可荣和的牌' };
  if (discard.bySeat === seat) return { legal: false, reason: '不能荣和自己的牌' };

  const player = state.players[seat];

  // ── 振听检查 ──
  if (player.furiten) {
    return { legal: false, reason: '振听中，不能荣和' };
  }

  // ── 舍牌振听（自己的河里或副露中出现过听牌张）──
  const counts = handToCounts(player.hand);
  const analysis = analyzeWaits(counts, player.melds);
  const waitTiles = analysis.waits.map((w) => w.tile);

  if (waitTiles.length === 0) {
    return { legal: false, reason: '手牌未听牌' };
  }

  const discardIdx = tileIndex(discard.tile);
  if (!waitTiles.includes(discardIdx)) {
    return { legal: false, reason: `${indexToName(discardIdx)} 不是听牌张` };
  }

  // 舍牌振听：自己的舍牌中有任何一张听牌张
  const ownDiscards = new Set(player.discards.map(tileIndex));
  if (waitTiles.some((w) => ownDiscards.has(w))) {
    return { legal: false, reason: '舍牌振听（自己的舍牌中含听牌张）' };
  }

  // 自家副露中含听牌张（简化：也算振听）
  // 严格规则：只有舍牌振听，副露不算。此处按标准实现，仅检查舍牌。

  // ⚠️ 无役不能和：荣和同样需要至少一役（原来完全没查 → 无役也能荣和）。
  if (!hasYaku(state, seat, discard.tile, false)) {
    return { legal: false, reason: '无役不能和（没有役种；宝牌不算役）' };
  }

  return { legal: true };
}

/**
 * 当前弃牌的荣和候选者（按座位序）
 *
 * 供「三家和」判定 + UI 提示：一次弃牌可能有多个家同时满足荣和条件。
 * ⚠️ 引擎一次只结算一个 ron（由 driver 决定先后）；
 *    三家和是否按流局处理由规则预设 `sanchahoIsDraw` 决定。
 */
export function ronCandidates(state: GameState): Seat[] {
  if (state.phase !== 'claim' || !state.lastDiscard) return [];
  const out: Seat[] = [];
  for (const p of state.players) {
    if (p.seat === state.lastDiscard.bySeat) continue;
    if (canRon(state, p.seat).legal) out.push(p.seat);
  }
  return out;
}

// ════════════════════════════════════════════════════════════════
// 统一入口
// ════════════════════════════════════════════════════════════════

/**
 * 统一合法性检查
 */
export function canApply(state: GameState, action: Action, seat?: Seat): ActionValidation {
  const actor = seat ?? state.activeSeat;
  switch (action.type) {
    case 'draw':
      return state.phase === 'draw'
        ? { legal: true }
        : { legal: false, reason: `阶段 ${state.phase} 不能摸牌` };
    case 'discard': {
      if (actor !== state.activeSeat) return { legal: false, reason: '不是你的回合' };
      if (action.riichi) {
        const v = canRiichiWithDiscard(state, action.tile);
        if (!v.legal) return v;
      }
      return canDiscard(state, action.tile);
    }
    case 'tsumo':
      return canTsumo(state);
    case 'ron':
      return canRon(state, actor);
    case 'chi':
      return canChi(state, actor, action.tiles);
    case 'pon':
      return canPon(state, actor);
    case 'kan':
      if (action.kind === 'ankan') return canAnkan(state, action.tile);
      if (action.kind === 'kakan') return canKakan(state, action.tile);
      return canMinkan(state, actor);
    case 'pass':
      return { legal: true };
    case 'riichi_declare':
      return canRiichi(state);
    case 'abortive_draw': {
      if (action.reason === 'nine_terminals') {
        // 九种九牌：自己第一巡摸牌后宣言
        if (state.phase !== 'discard') return { legal: false, reason: '当前不能宣言九种九牌' };
        if (actor !== state.activeSeat) return { legal: false, reason: '不是你的回合' };
        return canNineTerminals(state, actor)
          ? { legal: true }
          : { legal: false, reason: '手牌中幺九牌种类不足 9（或已鸣牌/已打牌）' };
      }
      // 三家和：仅当规则预设把三家和处理为流局时（天凤）且确有 ≥3 家可荣和
      if (state.phase !== 'claim') return { legal: false, reason: '当前不能宣言三家和' };
      if (!rulesOf(state).sanchahoIsDraw) {
        return { legal: false, reason: '当前预设（雀魂）三家和按荣和计，不流局' };
      }
      return checkTripleRon(ronCandidates(state))
        ? { legal: true }
        : { legal: false, reason: '荣和候选不足 3 家' };
    }
    default: {
      const _e: never = action;
      return { legal: false, reason: `未知动作 ${JSON.stringify(_e)}` };
    }
  }
}

// ════════════════════════════════════════════════════════════════
// 鸣牌优先级
// ════════════════════════════════════════════════════════════════

/** 鸣牌优先级（数值越大越优先） */
export const CLAIM_PRIORITY = {
  ron: 3, // 荣和最高
  kan: 2, // 碰/杠
  pon: 2,
  chi: 1, // 吃最低
} as const;

/**
 * 决定谁有鸣牌优先权
 *
 * @param candidates 所有想鸣牌的玩家及其动作
 * @returns 优先权最高的玩家及动作（同优先级按座位顺序，从弃牌者下家开始）
 */
export function resolveClaimPriority(
  discarderSeat: Seat,
  candidates: ReadonlyArray<{ seat: Seat; action: Action }>,
): { seat: Seat; action: Action } | null {
  if (candidates.length === 0) return null;

  // 座位顺序：从弃牌者下家开始
  const seatOrder = [1, 2, 3].map((d) => ((discarderSeat + d) % 4) as Seat);

  const scored = candidates.map((c) => ({
    ...c,
    priority: claimPriorityOf(c.action),
    order: seatOrder.indexOf(c.seat),
  }));

  scored.sort((a, b) => b.priority - a.priority || a.order - b.order);
  const best = scored[0];
  return { seat: best.seat, action: best.action };
}

/** 动作的鸣牌优先级 */
function claimPriorityOf(action: Action): number {
  switch (action.type) {
    case 'ron':
      return CLAIM_PRIORITY.ron;
    case 'pon':
      return CLAIM_PRIORITY.pon;
    case 'kan':
      return CLAIM_PRIORITY.kan;
    case 'chi':
      return CLAIM_PRIORITY.chi;
    default:
      return 0;
  }
}

// ════════════════════════════════════════════════════════════════
// 食替检查
// ════════════════════════════════════════════════════════════════

/**
 * 食替判定：吃了 123m 后不能立即打 4m（同为 123m 的延展）
 *
 * 规则：吃牌后，不能在同一巡打出与所吃顺子"相连"的牌，
 *      使得该顺子变成另一个顺子（等于没有真正使用吃来的牌）。
 */
export function isKuikae(
  meldStart: number,
  discardTile: number,
): boolean {
  // 吃的顺子是 [start, start+1, start+2]
  // 禁止打出 start-1 或 start+3（同花色内）
  const suitStart = Math.floor(meldStart / 9) * 9;
  const posInSuit = meldStart - suitStart;
  const dSuit = Math.floor(discardTile / 9);
  if (dSuit !== Math.floor(meldStart / 9)) return false; // 不同花色
  const dPos = discardTile - suitStart;

  // 禁止 start-1
  if (dPos === posInSuit - 1 && posInSuit - 1 >= 0) return true;
  // 禁止 start+3
  if (dPos === posInSuit + 3 && posInSuit + 3 <= 8) return true;

  return false;
}

/**
 * 检查打牌是否构成食替
 */
export function canDiscardAfterMeld(
  state: GameState,
  tile: Tile,
  justMelded: { start: number; isChi: boolean } | null,
): ActionValidation {
  if (!justMelded || !justMelded.isChi) return { legal: true };
  if (isKuikae(justMelded.start, tileIndex(tile))) {
    return {
      legal: false,
      reason: `食替：刚吃 ${indexToName(justMelded.start)} 起，不能打 ${indexToName(tileIndex(tile))}`,
    };
  }
  return { legal: true };
}
